import { useState } from "react";
import { useHistory } from "react-router-dom";
import { useMeta, useNotification, useForm, useValidator } from "@/hooks";
import { Tabs, Icon } from "@/components";
import { HeaderActions, Heading, Panel, Text } from "@/ui";
import { CcpaDeleteModal, useCcpaDeleteCustomer } from "./ccpa";
import routes from "../../config/routes/routes";
import {
    QUERY_GET_CUSTOMER,
    QUERY_GET_IDP_ACCOUNT,
    MUTATION_UPDATE_IDP_ACCOUNT,
} from "../../apollo/module-operations/customer";
import {
    MUTATION_UPDATE_ACCOUNT,
    MUTATION_FORGOT_USER_PASSWORD,
    MUTATION_UPDATE_ACCOUNT_BERBIX_VERIFICATION_STATUS,
} from "../../apollo/module-operations/account";
import { Content } from "@/layout";
import { useLazyQuery, useQuery, useMutation } from "@apollo/react-hooks";
import GeneralPane from "./tabs_panels/GeneralPane";
import VerificationPane from "./tabs_panels/VerificationPane";
import ActivityLogs from "./tabs_panels/ActivityLogs";
import Notes from "./tabs_panels/Notes";
import { ROLES } from "config/constant";
import styles from "./CustomerForm.module.scss";

const SaveIcon = () => <Icon icon="save" />;
const AlertIcon = () => <Icon icon="alert" />;
const DeleteIcon = () => <Icon icon="delete" />;

const CustomerForm = () => {
    const [loading, setLoading] = useState(true);
    const [statusBerbix, setStatusBerbix] = useState(null);

    const { user, resourceId } = useMeta();
    const history = useHistory();
    const notify = useNotification();

    const { inputs, setInputs, inputChange, datepickerChange, selectChange } =
        useForm();

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "first_name",
                method: "isEmpty",
                validWhen: false,
                message: "This name is required.",
            },
            {
                field: "last_name",
                method: "isEmpty",
                validWhen: false,
                message: "This last name is required.",
            },
            {
                field: "phone",
                method: "isEmpty",
                validWhen: false,
                message: "This phone number is required.",
            },
            {
                field: "date_of_birth",
                method: "isEmpty",
                validWhen: false,
                message: "This date of birth is required.",
            },
            {
                field: "role",
                method: "isEmpty",
                validWhen: false,
                message: "This role is required.",
            },
        ],
        inputs,
    );

    const options = {
        account_id: user.accountId,
        store_id: user.storeId,
        id: resourceId,
    };

    const { loading: getCustomerLoading, refetch } = useQuery(
        QUERY_GET_CUSTOMER,
        {
            variables: options,
            skip: user.loading || !user.accountId || !user.storeId,
            fetchPolicy: "network-only",
            onCompleted: resp => {
                const account = resp?.getAccount;
                setInputs(account);

                getIdPAccount({
                    variables: {
                        account_id: user.accountId,
                        store_id: user.storeId,
                        email: account?.email,
                    },
                });
            },
        },
    );

    const [getIdPAccount, { loading: loadingIDPAccount }] = useLazyQuery(
        QUERY_GET_IDP_ACCOUNT,
        {
            fetchPolicy: "network-only",
            onCompleted: resp => {
                const idPAccount = resp.getIDPAccount;

                setInputs(prev => ({
                    ...prev,
                    berbix_id: idPAccount?.berbix_id || null,
                    berbix_verified: idPAccount?.berbix_verified || false,
                    berbix_error: idPAccount?.berbix_error || null,
                    email_verified: idPAccount?.email_verified || false,
                    enabled: idPAccount?.enabled || false,
                    id: idPAccount?.id || null,
                    role: idPAccount?.role || null,
                }));
                setStatusBerbix(idPAccount?.berbix_verified || false);
                setLoading(false);
            },
            onError: () => {
                setInputs(prev => ({
                    ...prev,
                    role: ROLES[0].value,
                }));
                setLoading(false);
            },
        },
    );

    const getAccountUpdateInput = (newTreezIds = null) => {
        return {
            first_name: inputs.first_name,
            last_name: inputs.last_name,
            email: inputs.email,
            phone: inputs.phone,
            driver_license_id: inputs.driver_license_id,
            latitude: inputs.latitude,
            longitude: inputs.longitude,
            address1: inputs.address1,
            address2: inputs.address2,
            city: inputs.city,
            city_code: inputs.city_code,
            country: inputs.country,
            country_code: inputs.country_code,
            province: inputs.province,
            province_code: inputs.province_code,
            zip: inputs.zip,
            tags: inputs.tags,
            accept_marketing: inputs.accept_marketing,
            photo: inputs.photo,
            driver_license_photo: inputs.driver_license_photo,
            date_of_birth: inputs.date_of_birth,
            treez_ids: newTreezIds !== null ? newTreezIds : inputs.treez_ids,
        };
    };

    const [resetPassword, { loading: loadingReset }] = useMutation(
        MUTATION_FORGOT_USER_PASSWORD,
        {
            refetchQueries: [],
            onCompleted: () => {
                notify("Reset Password Sent Successfully", "success");
            },
            onError: () => {
                notify(
                    "Error Sending the Reset Password Email to the customer",
                    "error",
                );
            },
        },
    );

    const [mutateIDP, { loading: loadingIDP }] = useMutation(
        MUTATION_UPDATE_IDP_ACCOUNT,
        {
            onCompleted: () => {
                notify("Customers Updated Successfully", "success");
                setInputs({
                    ...inputs,
                    enabled: !inputs?.enabled,
                });
            },
            onError: () => {
                notify("Error Updating Customer", "error");
            },
        },
    );

    const [
        mutateChangeBerbixVerificationStatus,
        { loading: loadingChangeBerbixVerificationstatus },
    ] = useMutation(MUTATION_UPDATE_ACCOUNT_BERBIX_VERIFICATION_STATUS, {
        onCompleted: () => {
            setInputs({ ...inputs, note: "" });
            setStatusBerbix(inputs.berbix_verified);
            notify(
                "Account Verification Status Updated Successfully",
                "success",
            );
        },
        onError: error => {
            notify(error.message, "error");
        },
    });

    const [mutate, { loading: loadingUpdate }] = useMutation(
        MUTATION_UPDATE_ACCOUNT,
        {
            onCompleted: () => {
                refetch();
                notify("Account Updated Successfully", "success");
            },
            onError: () => {
                notify("Error Updating The Customer", "error");
            },
        },
    );

    const sendTheResetPasswordEmail = async () => {
        resetPassword({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                email: inputs.email,
            },
        });
    };

    const updateCustomers = async () => {
        if (isSubmited) {
            isSubmited(true);
        }

        if (isValid()) {
            mutate({
                variables: {
                    account_id: user.accountId,
                    store_id: user.storeId,
                    input: getAccountUpdateInput(null),
                },
            });
        }
    };

    const updateTreezIds = async newTreezIds => {
        if (isSubmited) {
            isSubmited(true);
        }

        if (isValid()) {
            mutate({
                variables: {
                    account_id: user.accountId,
                    store_id: user.storeId,
                    input: getAccountUpdateInput(newTreezIds),
                },
            });
        }
    };

    const updateIDPAccountStatus = () => {
        mutateIDP({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: {
                    email: inputs.email,
                    enable: !inputs?.enabled,
                },
            },
        });
    };

    const changeBerbixVerificationStatus = () => {
        mutateChangeBerbixVerificationStatus({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: {
                    email: inputs.email,
                    accept: inputs.berbix_verified,
                    note: inputs.note || null,
                },
            },
        });
    };

    const showStatusAction = !loadingIDPAccount && !getCustomerLoading;
    const { openDelete, modalProps } = useCcpaDeleteCustomer({
        onCloseSuccess: () => history.push(routes.CUSTOMER_LIST.route),
    });

    const headerActions = [];

    if (showStatusAction) {
        headerActions.push({
            id: "toggle-status",
            label: inputs?.enabled ? "Deactivate" : "Activate",
            icon: <AlertIcon />,
            loading: loadingIDP,
            disabled: loadingIDP || loadingUpdate,
            onClick: updateIDPAccountStatus,
            variant: "secondary",
            tooltip: inputs?.enabled
                ? "Disables this customer's account so they cannot sign in."
                : "Enables this customer's account so they can sign in.",
        });
    }

    headerActions.push({
        id: "delete-ccpa",
        label: "Delete customer data",
        icon: <DeleteIcon />,
        disabled: loadingUpdate || loadingIDP || !inputs.entity_id,
        onClick: () =>
            openDelete({
                email: inputs.email,
                id: inputs.entity_id,
                name: `${inputs.first_name ?? ""} ${inputs.last_name ?? ""}`
                    .replace(/\s+/g, " ")
                    .trim(),
            }),
        variant: "secondary",
        destructive: true,
        tooltip:
            "This action permanently deletes this customer's data from the store and cannot be undone.",
    });

    headerActions.push({
        id: "update",
        label: "Update",
        icon: loadingUpdate ? undefined : <SaveIcon />,
        loading: loadingUpdate,
        disabled: loadingUpdate || loadingIDP,
        onClick: updateCustomers,
        variant: "primary",
    });

    return (
        <Content>
            <Panel
                className={`customers__details ${styles.root}`}
                header={
                    <div className={styles.headerBar}>
                        <div className={styles.headerText}>
                            <Heading level={4}>Customer</Heading>
                            <Text tone="secondary" variant="body-md">
                                Customer details
                            </Text>
                        </div>
                        <HeaderActions
                            actions={headerActions}
                            aria-label="Customer form actions"
                        />
                    </div>
                }
                showHeaderDivider
            >
                <Tabs
                    navs={[
                        {
                            title: "General",
                            content: (
                                <GeneralPane
                                    datepickerChange={datepickerChange}
                                    inputChange={inputChange}
                                    inputs={inputs}
                                    loading={loading}
                                    loadingReset={loadingReset}
                                    loadingUpdate={loadingUpdate || loadingIDP}
                                    resetPassword={sendTheResetPasswordEmail}
                                    selectChange={selectChange}
                                    updateTreezIds={updateTreezIds}
                                    validator={validator}
                                />
                            ),
                        },
                        {
                            title: "Verification",
                            content: (
                                <VerificationPane
                                    changeBerbixVerificationStatus={
                                        changeBerbixVerificationStatus
                                    }
                                    inputChange={inputChange}
                                    inputs={inputs}
                                    loading={loading}
                                    loadingReset={
                                        loadingChangeBerbixVerificationstatus
                                    }
                                    selectChange={selectChange}
                                    statusBerbix={statusBerbix}
                                    validator={validator}
                                />
                            ),
                        },
                        {
                            title: "Notes",
                            content: (
                                <Notes
                                    customers={inputs}
                                    loading={getCustomerLoading}
                                    setCustomers={setInputs}
                                    user={user}
                                />
                            ),
                        },
                        {
                            title: "Activity Logs",
                            content: (
                                <ActivityLogs
                                    inputs={inputs}
                                    loading={getCustomerLoading}
                                />
                            ),
                        },
                    ]}
                />
            </Panel>
            <CcpaDeleteModal {...modalProps} />
        </Content>
    );
};

export default CustomerForm;
